import React, { FC, useEffect, useState } from "react";
import { Row, Col, Button, Modal, ModalBody } from "reactstrap";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";

import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormInput from "../../form-components/InputComponent";
import { Checkbox, message, Popover, Radio, Select, Tag } from "antd";
import FormDatePicker from "../../form-components/DatePicker";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";
import FormCheckbox from "../../form-components/CheckboxComponent";
import { projectsApis } from "../../../api/projects";
import { tagsApis } from "../../../api/Tags";
import { ApiResponse } from "../../../api/master/axiosIndex";
import Swal from "sweetalert2";
import { projectGroupApis } from "../../../api/ProjectGroup";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import ProjectApprovalDrawer from "./ProjectApprovalDrawer";
import useSequenceNumbering from "../../../hooks/useSequenceNumbering";
import { SequenceInput, SequenceConfigModal } from "../../../components/sequence";

const { Option } = Select;

const tagColors = [
  "#f28585", // red
  "#f2a679", // orange
  "#f2d479", // yellow
  "#a6f279", // green
  "#79f2f2", // teal
  "#79a6f2", // blue
  "#a679f2", // purple
  "#f279c4", // pink
];

// Get color by hash of label
const getColorByLabel = (label: string) => {
  let sum = 0;
  for (let i = 0; i < label.length; i++) {
    sum += label.charCodeAt(i);
  }
  return tagColors[sum % tagColors.length];
};
export const tagRender = ({ label, closable, onClose }: CustomTagProps) => {
  const color = getColorByLabel((label || "")?.toString());

  return (
    <span className="custom-tag-wrapper">
      <Tag
        className="custom-tag"
        style={{ backgroundColor: color }}
        closable={closable}
        onClose={onClose}
      >
        {label}
      </Tag>
      <span className="custom-tag-tail" style={{ borderLeftColor: color }} />
    </span>
  );
};

const ProjectCreation = ({ onSuccess }: any) => {
  const navigate = useNavigate();
  const formMethods = useForm({
    mode: "onChange",
  });
  const { watch, register, reset, setValue, getValues, handleSubmit, control } =
    formMethods;
  const [popoverVisible, setPopoverVisible] = useState(false);

  // Document numbering: auto-generate when a "Project" sequence is configured,
  // otherwise fall back to manual entry so creation is never blocked.
  const {
    numberingMode,
    showNumberingModal,
    tempNumberingMode,
    setTempNumberingMode,
    activeSequences,
    currentSequence,
    canAuto,
    fetchSequenceAccess,
    openModal,
    closeModal,
    saveModalSelection,
    buildPayload,
  } = useSequenceNumbering({
    moduleName: "Project",
    fieldName: "referenceID",
    level: "Project",
    setValue,
    getValues,
  });

  useEffect(() => {
    fetchSequenceAccess();
  }, [fetchSequenceAccess]);

  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [sitesList, setSitesList] = useState<SelectOption[]>([]);
  const [ownersList, setOwnersList] = useState<SelectOption[]>([]);
  const [calendarList, setCalendarList] = useState<SelectOption[]>([]);
  const [ratecardsList, setRatecardsList] = useState<SelectOption[]>([]);
  const [groupsList, setGroupsList] = useState<SelectOption[]>([]);
  const [budgetList, setBudgetList] = useState<SelectOption[]>([]);
  const [boqList, setBoqList] = useState<SelectOption[]>([]);
  const [projectGropusList, setProjectGroupsList] = useState<SelectOption[]>(
    []
  );
  const [approvals, setApprovals] = useState({
    open: false,
    data: [],
  });
  const [loading, setLoading] = useState(false);
  const [warningWage, setWarningWage] = useState<{
    error: any | undefined;
    message: string | undefined;
  }>({
    error: undefined,
    message: undefined,
  });

  const [tags, setTags] = useState<any[]>([]);
  const [tagsOptions, setTagsOptions] = useState<SelectOption[]>([]);

  const isStrictWatch = watch("isStrict");
  const projectOwnerIdWatch = watch("projectOwnerId");
  const siteIdWatch = watch("siteId");
  const plannedStartDateWatch = watch("plannedStartDate");
  const plannedEndDateWatch = watch("plannedEndDate");

  const handleChange = (value: any) => {
    setTags(value);
  };

  const createNewGroup = async (newGroupName: string) => {
    const newGroup = {
      value: newGroupName,
      label: newGroupName,
    };
    try {
      const response = await projectGroupApis.createProjectGroup({
        projectGroupName: newGroupName,
      });
      const { status } = response;
      if (status) {
        message.success("Group created successfully");
        fetchProjectGroupDropDown();
        setPopoverVisible(false);
      }
    } catch (error) {}
    // call API to create new group
    setProjectGroupsList((prevGroups) => [...prevGroups, newGroup]);
    // setValue("projectGroupId", newGroupName);
    setValue("newGroupName", undefined);
  };

  const submitForm = (formData: any) => {
    console.log("Submit:::Data", formData, tags);
    const {
      projectOwnerId,
      users,
      mustFinishDate,
      plannedStartDate,
      isStrict,
      plannedEndDate,
      approvalRequired,
      rateCardId,
      boqs,
    } = formData;
    const approvalsData = approvals.data;
    const tagsData = tags.map((item: any) => {
      return {
        tagName: item,
      };
    });
    console.log("boqs", boqs);
    const transformedBoqs =
      boqs?.map((targetId: number) => {
        return {
          boqTargetId: targetId,
        };
      }) || [];
    const formattedUsers = [
      {
        userId: parseInt(projectOwnerId, 10),
        projectUserType: "Owner",
      },
      ...users
        .filter((id: any) => id !== projectOwnerId) // Avoid duplicating owner
        .map((id: any) => ({
          userId: parseInt(id, 10),
          projectUserType: "Resource Head",
        })),
    ];
    delete formData.approvalsData;
    const finalPayload = {
      ...formData,
      users: formattedUsers,
      mustFinishDate: isStrict
        ? mustFinishDate && dayjs(mustFinishDate).format("YYYY-MM-DD")
        : null,
      plannedStartDate: dayjs(plannedStartDate).format("YYYY-MM-DD"),
      plannedEndDate: dayjs(plannedEndDate).format("YYYY-MM-DD"),
      mode: "F",
      approvalRequired: approvalRequired === "yes" ? true : false,
      autoApprovalRemarks:
        approvalRequired === "no" ? formData.autoApprovalRemarks : undefined,
      ratecards: rateCardId ? [{ rateCardId: rateCardId }] : [],
      boqs: transformedBoqs,
      approvals: approvalsData,
      tags: tagsData,
    };
    // Strip referenceID when auto-numbering (server assigns it); keep it when
    // the user entered it manually.
    const payloadWithNumbering = buildPayload(finalPayload);
    delete payloadWithNumbering.numberingMode;
    console.log("Payload to submit:", payloadWithNumbering);
    createProject(payloadWithNumbering);
  };

  const createProject = async (Payload: any) => {
    try {
      const response: ApiResponse = await projectsApis.createProject(Payload);
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        reset();
        onSuccess();
      } else {
        // setWarningWage({
        //   message,
        //   error,
        // });
        Swal.fire({
          icon: "error",
          title: "Error",
          text: message,
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteUsersBySiteId = async (siteId: string | number) => {
    try {
      const response = await projectsApis.fetchSiteUsers(siteId);
      const { data, status } = response || {};
      if (status) {
        setOwnersList(
          data?.map((item: any) => ({
            label: item.userName,
            value: item.appUserId,
          }))
        );
        setValue("projectOwnerId", undefined);
      }
    } catch (error) {}
  };

  const fetchRateCardDropDown = async () => {
    try {
      const response = await projectsApis.fetchRateCardDropDown();
      const { data, status } = response || {};
      if (status) {
        setRatecardsList(
          data?.map((item: any) => ({
            label: item.rateCardTitle,
            value: item.rateCardId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchProjectGroupDropDown = async () => {
    try {
      const response = await projectsApis.fetchProjectGroupDropDown();
      const { data, status } = response || {};
      if (status) {
        setGroupsList(
          data?.map((item: any) => ({
            label: item.projectGroupName,
            value: item.projectGroupId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchProjectCalendarDropDown = async () => {
    try {
      const response = await projectsApis.fetchProjectCalendarDropDown();
      const { data, status } = response || {};
      if (status) {
        setCalendarList(
          data?.map((item: any) => ({
            label: item.calendarTitle,
            value: item.calendarId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchMaterialBoqDropDown = async (siteIds: any) => {
    try {
      const response = await projectsApis.fetchMaterialBoqDropDown(siteIds);
      const { data, status } = response || {};
      if (status) {
        setBoqList(
          data?.map((item: any) => ({
            label: item.boqTitle,
            value: item.targetId,
          }))
        );
      }
    } catch (error) {}
  };

  const fetchBudgetDropDown = async (siteId: any) => {
    try {
      const response = await projectsApis.fetchbudgetDropDown(siteId);
      const { data, status } = response || {};
      if (status) {
        const items = Array.isArray(data) ? data : data ? [data] : [];
        const options = items.map((item: any) => ({
          label: item.budgetName,
          value: item.headerId,
        }));
        setBudgetList(options);
        if (options.length === 1) {
          setValue("budgetId", options[0].value, { shouldValidate: true });
        } else {
          setValue("budgetId", undefined);
        }
      }
    } catch (error) {}
  };

  const fetchProjectTags = async () => {
    try {
      let tagsList: { tagId: string; tagName: string }[] = [];
      const response: any = await projectsApis.fetchProjectTags(0);
      const { data, status } = response || {};
      if (status && Array.isArray(data)) {
        tagsList = data;
      }
      if (tagsList.length === 0) {
        const tagsResponse: any = await tagsApis.tagsList();
        const { data: tagsData, status: tagsStatus } = tagsResponse || {};
        if (tagsStatus && Array.isArray(tagsData)) {
          tagsList = tagsData;
        }
      }
      if (tagsList.length > 0) {
        setTagsOptions(
          tagsList.map((item: { tagId: string; tagName: string }) => ({
            label: item.tagName,
            value: item.tagName,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching project tags:", error);
    }
  };

  const fetchSitesUnlinkedProjects = async () => {
    try {
      const response = await projectsApis.fetchSitesUnlinkedProjects();
      const { data, status } = response || {};
      if (status) {
        setSitesList(
          data?.map((item: any) => ({
            label: item.name,
            value: item.siteId,
          }))
        );
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchSitesUnlinkedProjects();
  }, []);

  useEffect(() => {
    if (siteIdWatch) {
      fetchSiteUsersBySiteId(siteIdWatch);
      fetchMaterialBoqDropDown(siteIdWatch);
      fetchBudgetDropDown(siteIdWatch);
    }
  }, [siteIdWatch]);

  useEffect(() => {
    fetchRateCardDropDown();
    fetchProjectGroupDropDown();
    fetchProjectCalendarDropDown();
    fetchProjectTags();
  }, []);

  const resourceHeads = ownersList?.filter(
    (item) => item.value != projectOwnerIdWatch
  );

  return (
    <>
      <div className="mt-3">
        <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
          <FormProvider {...formMethods}>
            <form
              className="content clearfix"
              onSubmit={handleSubmit(submitForm)}
            >
              <Row className="gy-4">
                <Col lg="8">
                  <FormInput
                    name="projectTitle"
                    label="Project Title"
                    required={true}
                    type="text"
                    placeholder="Enter Project Title"
                    // tooltipText="Enter the Project Title for the Project"
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="siteId"
                    label={`Site`}
                    options={sitesList}
                    required={true}
                    placeholder="-Select-"
                    // tooltipText="Select the site."
                  />
                </Col>
                <Col lg="4">
                  <SequenceInput
                    name="referenceID"
                    label="Project Number"
                    numberingMode={numberingMode}
                    onSettingsClick={openModal}
                  />
                </Col>
                <Col lg="4">
                  <FormDatePicker
                    name="plannedStartDate"
                    label="Planned Start Date"
                    required={true}
                  />
                </Col>
                <Col lg="4">
                  <FormDatePicker
                    name="plannedEndDate"
                    label="Planned End Date"
                    required={true}
                    minDate={watch("plannedStartDate")}
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="calendarId"
                    label={`Calendar`}
                    options={calendarList}
                    popOverOptions={
                      <Popover
                        content="Click here to create a new calendar"
                        title="Add New Calendar"
                      >
                        <a
                          style={{
                            cursor: "pointer",
                            color: "#1890ff",
                          }}
                          onClick={() => {
                            navigate("/pms/calendar");
                          }}
                        >
                          Add new Calendar
                        </a>
                      </Popover>
                    }
                    required={false}
                    placeholder="-Select-"
                    // tooltipText="Select the Calender for the Project."
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="rateCardId"
                    label={`Rate Card`}
                    options={ratecardsList}
                    required={false}
                    placeholder="-Select-"
                    // tooltipText="Select the Calender for the Project."
                  />
                </Col>

                <Col lg="8">
                  <FormInput
                    name="projectDescription"
                    label="Description"
                    required={true}
                    type="text"
                    minLength={2}
                    maxLength={200}
                    placeholder="Enter Description"
                    // tooltipText="Enter a brief description of the Project."
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="budgetId"
                    label="Budget"
                    options={budgetList}
                    required={true}
                    placeholder="Select Budget"
                    // tooltipText="Select the Budget for the Project."
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="projectGroupId"
                    label={`Group`}
                    options={groupsList}
                    popOverOptions={
                      <Popover
                        content={
                          <Row>
                            <Col lg="8">
                              <FormInput
                                label="Group Name"
                                name="newGroupName"
                                required={true}
                                type="text"
                              />
                            </Col>
                            <Col lg="4" className="mt-4">
                              <Button
                                // color="primary"
                                type="button"
                                onClick={() => {
                                  const newGroupName = watch("newGroupName");
                                  if (newGroupName) {
                                    createNewGroup(newGroupName);
                                  }
                                }}
                              >
                                Add
                              </Button>
                            </Col>
                          </Row>
                        }
                        title=""
                        trigger="click"
                        open={popoverVisible}
                        onOpenChange={setPopoverVisible}
                      >
                        <a
                          style={{
                            cursor: "pointer",
                            color: "#1890ff",
                          }}
                        >
                          Add new Group
                        </a>
                      </Popover>
                    }
                    required={true}
                    placeholder="-Select-"
                    // tooltipText="Select the Calender for the Project."
                  />
                </Col>
                <Col lg="4">
                  <label className="form-label">Tags</label>
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Select or type to create a tag"
                    value={tags}
                    onChange={handleChange}
                    options={tagsOptions}
                    tokenSeparators={[","]}
                    tagRender={tagRender}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toString()
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="projectOwnerId"
                    label="Project Manager"
                    options={ownersList}
                    required={true}
                    placeholder="Select Owner"
                    // tooltipText="Select the Owner for the Project."
                  />
                </Col>
                {/* <Col lg="4" style={{ paddingTop: "2%", paddingLeft: "8%" }}>
                  <Controller
                    name="strictProject"
                    control={control}
                    defaultValue={[]} // Default selection (empty array)
                    render={() => (
                      <Checkbox value={"strictProject"} checked>
                        Make this a strict project
                      </Checkbox>
                    )}
                  />
                </Col> */}
                <Col lg="4" style={{ marginTop: "4%" }}>
                  <FormCheckbox
                    name="isStrict"
                    optionLabel="Make this a strict project"
                    noGap={true}
                    size={"small"}
                    // onChange={(e: any) => {
                    //   setValue("strictProject", e.target.checked);
                    // }}
                  />
                </Col>
                {isStrictWatch && (
                  <Col lg="4">
                    <FormDatePicker
                      name="mustFinishDate"
                      label="Must Finish By"
                      required={false}
                      minDate={plannedStartDateWatch}
                    />
                  </Col>
                )}
                <Col lg="4">
                  <FormSelect
                    name="approvalRequired"
                    label="Approval Required ?"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    placeholder="Select"
                    required={true}
                  />
                  <div style={{ display: "none" }}>
                    <FormInput name="approvalsData" label="" type="text" />
                  </div>
                  {watch("approvalRequired") == "yes" && (
                    <Popover
                      content="Click here to set required approvals"
                      title="Approval Required"
                    >
                      <a
                        href="#"
                        className="success"
                        onClick={(e) => {
                          e.preventDefault();
                          setApprovals((pre) => {
                            return {
                              ...pre,
                              open: true,
                            };
                          });
                        }}
                      >
                        <EditOutlined /> Set Approvals &nbsp;
                        {approvals?.data?.length == 0 ? (
                          <span className="mandatory">
                            Please set approvals
                          </span>
                        ) : (
                          <></>
                        )}
                      </a>
                    </Popover>
                  )}
                </Col>
                {watch("approvalRequired") == "no" && (
                  <Col lg="8">
                    <FormInput
                      name="autoApprovalRemarks"
                      label="Remarks for No Approval"
                      type="textarea"
                      rows={2}
                      required={true}
                      minLength={2}
                      maxLength={500}
                      placeholder="Enter reason for not requiring approvals"
                    />
                  </Col>
                )}
                <Col lg="8">
                  <FormMultiSelect
                    name="boqs"
                    label="Material BOQ"
                    options={boqList}
                    required={false}
                    placeholder="Select Material BOQ"
                  />
                </Col>
                {/* <Col lg="4">
                          <FormMultiSelect
                            name="resourceHeads"
                            label="Resource Heads"
                            options={[{ label: "Resource 1", value: "1" }]}
                            required={true}
                            placeholder="Select Resource Heads"
                          />
                        </Col> */}
                <Col lg="12">
                  <label className="form-label">
                    Set Up Resource{""}
                    <span className="mandatory">*</span>
                  </label>
                  <br />
                  {resourceHeads?.length > 0 ? (
                    <Controller
                      name="users"
                      control={control}
                      defaultValue={[]}
                      render={({ field }) => (
                        <Checkbox.Group {...field}>
                          <Row className="w-100">
                            {resourceHeads?.map((option) => (
                              <Col
                                key={option.value}
                                xs={12}
                                sm={8}
                                md={6}
                                lg={3}
                                style={{ paddingBottom: "10px" }}
                              >
                                <Checkbox
                                  value={option.value}
                                  style={{
                                    minWidth: "max-content",
                                  }}
                                >
                                  {option.label}
                                </Checkbox>
                              </Col>
                            ))}
                          </Row>
                        </Checkbox.Group>
                      )}
                    />
                  ) : (
                    <div style={{ color: "red" }}>
                      No other resource heads available.
                    </div>
                  )}
                </Col>

                <Col lg="6">
                  <div className="form-label-wrapper">
                    <label className="form-label">
                      Project Access <span className="mandatory">*</span>
                    </label>
                  </div>
                  <div className="form-control-wrap">
                    <Controller
                      name="isPrivate"
                      control={control}
                      defaultValue={"true"} // Default selection
                      render={({ field }) => (
                        <Radio.Group {...field}>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <Radio value={"true"}>
                              <strong>Private</strong>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // color: "#666",
                                }}
                              >
                                Only project users can view and access this
                                project.
                              </div>
                            </Radio>
                            <Radio value={"false"} disabled>
                              <strong>Public</strong>
                              <div
                                style={{
                                  fontSize: "12px",
                                  // color: "#666",
                                }}
                              >
                                Portal users can only view, follow, and comment
                                whereas project users will have complete access.
                              </div>
                            </Radio>
                          </div>
                        </Radio.Group>
                      )}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="gy-4 mt-1">
                <Col>
                  <Button color="primary" type="submit">
                    Submit
                  </Button>
                </Col>
              </Row>
            </form>
            {approvals.open && (
              <ProjectApprovalDrawer
                open={approvals.open}
                siteId={siteIdWatch}
                onClose={() => {
                  setApprovals((pre) => {
                    return {
                      ...pre,
                      open: false,
                    };
                  });
                }}
                onSubmit={(data: any) => {
                  console.log("data:111111", data);
                  setApprovals({
                    open: false,
                    data: data,
                  });
                }}
                setApprovalSubmissionStatus={(status: boolean) => {}}
                approvalData={approvals.data} // Pass the current approval data
              />
            )}
            <SequenceConfigModal
              moduleName="Project"
              fieldLabel="Project Number"
              show={showNumberingModal}
              tempNumberingMode={tempNumberingMode}
              setTempNumberingMode={setTempNumberingMode}
              canAuto={canAuto}
              currentSequence={currentSequence}
              activeSequences={activeSequences}
              onSave={saveModalSelection}
              onClose={closeModal}
            />
          </FormProvider>
        </div>
      </div>
      {/* </div>
          </div>
        </Card> */}
      {/* </Content> */}
    </>
  );
};

export default ProjectCreation;
